import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, SectionLabel } from "@/components/Reveal";
import { ASSETS } from "@/lib/site";

const STEPS = [
  ["Absorb", "Reduced L-Glutathione + Optiac™ deliver a bioavailable dose that reaches the bloodstream intact."],
  ["Neutralise", "Free radicals from sun and pollution are quenched before they can damage skin cells and collagen."],
  ["Inhibit", "Tyrosinase activity is calmed, slowing excess melanin and softening dark spots over time."],
  ["Illuminate", "With consistent use, tone evens out and skin reads brighter, clearer and visibly more youthful."],
];

export default function HowItWorks() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section
      id="how"
      ref={ref}
      data-testid="how-section"
      className="relative z-10 py-24 md:py-32"
    >
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
        {/* Clipped parallax frame */}
        <div className="relative h-[60vh] lg:h-[86vh] overflow-hidden rounded-sm border border-champagne lg:sticky lg:top-24">
          <motion.img
            src={ASSETS.lab}
            alt="Clinical formulation"
            style={{ y }}
            className="absolute inset-0 h-[116%] w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent" />
          <div className="absolute bottom-6 left-6 right-6 text-white">
            <div className="overline !text-white/80 mb-2">The Mechanism</div>
            <div className="font-serif text-2xl md:text-3xl font-light">
              Engineered for measurable radiance
            </div>
          </div>
        </div>

        <div>
          <SectionLabel index="03">How It Works</SectionLabel>
          <Reveal>
            <h2 className="font-serif font-light tracking-tight leading-[1.05] text-4xl md:text-5xl text-[color:var(--relumia-ink)] mb-4">
              Skin lightening, the{" "}
              <span className="italic text-gold">scientific</span> way.
            </h2>
          </Reveal>
          <Reveal delay={0.08}>
            <p className="text-[15px] leading-relaxed text-[color:var(--relumia-muted)] max-w-lg mb-12">
              Relumia works from within across four coordinated stages — no
              bleaching, no shortcuts. Just biochemistry doing what it does best.
            </p>
          </Reveal>

          <div className="space-y-px">
            {STEPS.map(([t, d], i) => (
              <Reveal key={t} delay={i * 0.08}>
                <div className="group flex gap-6 border-t border-champagne py-6 hover:bg-surface transition-colors px-2 -mx-2">
                  <div className="font-serif text-gold text-2xl w-10 shrink-0">
                    0{i + 1}
                  </div>
                  <div>
                    <div className="font-serif text-2xl text-[color:var(--relumia-ink)] mb-1">
                      {t}
                    </div>
                    <p className="text-[14.5px] leading-relaxed text-[color:var(--relumia-muted)] max-w-md">
                      {d}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
